//
// Copyright © 2026 PlayJoy Studios.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// Videos uploaded from this browser tab, kept so the author can watch and mark a video before its upload
// finishes (see timecode.ts). No component imports here: file.ts uses this module.

import { type Blob as PlatformBlob, type Ref } from '@hcengineering/core'
import { get, writable } from 'svelte/store'

/** @public */
export type LocalVideoState = 'uploading' | 'uploaded' | 'failed' | 'cancelled'

/**
 * A video uploaded from this browser tab. The file stays on the user's disk, so the author can watch it
 * and place timecodes before the upload finishes.
 * @public
 */
export interface LocalVideo {
  file: Ref<PlatformBlob>
  name: string
  type: string
  source: Blob
  progress: number
  state: LocalVideoState
  startedOn: number
}

/** @public */
export const localVideos = writable(new Map<string, LocalVideo>())

const localVideoUrls = new Map<string, string>()

const videoExtensions = /\.(mp4|m4v|mov|webm|mkv|avi|wmv|ogv|3gp)$/i

/** @public */
export function isVideoFile (name: string, type: string | undefined): boolean {
  return (type ?? '').startsWith('video/') || videoExtensions.test(name)
}

function hasUploadingVideos (): boolean {
  return Array.from(get(localVideos).values()).some((it) => it.state === 'uploading')
}

let unloadGuard = false

function guardUnload (): void {
  if (unloadGuard) return
  unloadGuard = true
  // Leaving the page drops the upload, and timecodes already written to this video stay without a file
  window.addEventListener('beforeunload', (event) => {
    if (hasUploadingVideos()) {
      event.preventDefault()
      event.returnValue = ''
    }
  })
}

/** @public */
export function trackLocalVideo (file: string, name: string, type: string, source: Blob): void {
  if (!isVideoFile(name, type)) return
  guardUnload()
  localVideos.update((map) =>
    map.set(file, {
      file: file as Ref<PlatformBlob>,
      name,
      type,
      source,
      progress: 0,
      state: 'uploading',
      startedOn: Date.now()
    })
  )
}

/** @public */
export function updateLocalVideo (file: string, update: Partial<Pick<LocalVideo, 'progress' | 'state'>>): void {
  if (!get(localVideos).has(file)) return
  localVideos.update((map) => {
    const video = map.get(file)
    if (video !== undefined) map.set(file, { ...video, ...update })
    return map
  })
}

/** @public */
export function getLocalVideoUrl (file: string): string | undefined {
  const video = get(localVideos).get(file)
  if (video === undefined) return undefined
  let url = localVideoUrls.get(file)
  if (url === undefined) {
    url = URL.createObjectURL(video.source)
    localVideoUrls.set(file, url)
  }
  return url
}
